import { useState, type FormEvent, type ReactNode } from 'react';
import { ArrowDown, ArrowUp, Check, FlaskConical, Plus, Trash2 } from 'lucide-react';
import { Button, Checkbox, Input, Notice, Select, Textarea } from '../ui';
import { Drawer } from '../ui/Drawer';
import { ListaTexto } from '../ui/ListaTexto';
import { Segmented } from '../ui/Segmented';
import { api, ApiError } from '../lib/api';
import { useResource } from '../lib/useResource';
import './automacao.css';

/* Espelha `automationActionSchema` do servidor. Não é uma lista aproximada: a
   tela anterior mostrava SEND_TEMPLATE, REMOVE_TAG, ASSIGN_AGENT, RESOLVE e
   PAUSE_AUTOMATION, cinco tipos que o schema NÃO aceita, e omitia três que
   aceita. Como aquela tela só lia, o erro nunca virou falha — o rótulo saía
   como o enum cru e ninguém notava. Num editor isso viraria uma regra recusada
   no envio, sem explicação útil. */
export type TipoAcao = 'SEND_TEXT' | 'SEND_BUTTONS' | 'ADD_TAG' | 'TRANSFER_HUMAN' | 'AI_REPLY' | 'SEND_FILE';

export const ROTULO_ACAO: Record<TipoAcao, string> = {
  SEND_TEXT: 'Responder com texto',
  SEND_BUTTONS: 'Responder com botões',
  ADD_TAG: 'Aplicar etiqueta',
  SEND_FILE: 'Enviar arquivo da biblioteca',
  AI_REPLY: 'Consultar agente de IA',
  TRANSFER_HUMAN: 'Transferir para atendimento humano',
};

export const ROTULO_CASAMENTO: Record<string, string> = {
  CONTAINS: 'Contém', EXACT: 'Igual a', STARTS_WITH: 'Começa com',
};

export interface Acao {
  type: TipoAcao;
  body?: string;
  footer?: string;
  buttons?: Array<{ id: string; title: string }>;
  tagId?: string;
  fileId?: string;
  caption?: string;
  agentKey?: string;
  integrationId?: string;
  instructions?: string;
}

export interface Regra {
  id: string; name: string; enabled: boolean; priority: number;
  matchType: string; keywords: string[];
  conditions: { phoneNumberIds: string[]; tagIds: string[]; schedule?: Horario };
  actions: Acao[];
}

interface Horario { days: number[]; start: string; end: string; timezone: string }
interface Tag { id: string; name: string }
interface Arquivo { id: string; originalName: string }
interface Integracao { id: string; name: string; kind: string; enabled: boolean }
interface Agente { key: string; name: string; enabled: boolean }
interface Canal { id: string; phoneNumbers: Array<{ id: string; displayNumber: string }> }

const DIAS = [
  { valor: 0, rotulo: 'Dom' }, { valor: 1, rotulo: 'Seg' }, { valor: 2, rotulo: 'Ter' },
  { valor: 3, rotulo: 'Qua' }, { valor: 4, rotulo: 'Qui' }, { valor: 5, rotulo: 'Sex' },
  { valor: 6, rotulo: 'Sáb' },
];

const FUSO = typeof Intl !== 'undefined'
  ? Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Sao_Paulo'
  : 'America/Sao_Paulo';

function acaoNova(tipo: TipoAcao): Acao {
  if (tipo === 'SEND_BUTTONS') return { type: tipo, body: '', buttons: [{ id: 'opcao_1', title: '' }] };
  if (tipo === 'AI_REPLY') return { type: tipo, agentKey: '', instructions: '' };
  return { type: tipo };
}

/**
 * Editor de automação.
 *
 * A tela de Automação mostrava palavras-chave, condições e cadeia de ações de
 * regras que só podiam nascer por `curl`: o servidor tinha `POST`, `PUT`,
 * `DELETE` e um simulador, e a interface oferecia apenas um interruptor.
 *
 * O formulário reproduz as regras do schema em vez de deixá-las virar 400:
 *
 * - Ao menos uma palavra-chave e ao menos uma ação, no máximo oito.
 * - A transferência para humano é única e precisa ser a ÚLTIMA — depois dela
 *   nada mais roda, então uma etiqueta agendada em seguida nunca seria
 *   aplicada. A ordem é editável porque ela muda o resultado.
 * - Horário é opcional; quando existe, exige dias, início, fim e fuso.
 */
export function FormularioAutomacao({
  regra, onFechar, onSalvo,
}: { regra?: Regra | null; onFechar: () => void; onSalvo: (mensagem: string) => void }) {
  const editando = Boolean(regra);
  const tags = useResource<Tag[]>('/workspace/tags', { vazio: () => false });
  const arquivos = useResource<{ files: Arquivo[] }>('/workspace/files', { vazio: () => false });
  const integracoes = useResource<Integracao[]>('/integrations', { vazio: () => false });
  const agentes = useResource<Agente[]>('/ai-agents', { vazio: () => false });
  const canais = useResource<Canal[]>('/workspace/channels', { vazio: () => false });

  const [nome, setNome] = useState(regra?.name ?? '');
  const [palavras, setPalavras] = useState<string[]>(regra?.keywords ?? []);
  const [casamento, setCasamento] = useState<string>(regra?.matchType ?? 'CONTAINS');
  const [prioridade, setPrioridade] = useState(String(regra?.priority ?? 100));
  const [ativa, setAtiva] = useState(regra?.enabled ?? true);
  const [numeros, setNumeros] = useState<string[]>(regra?.conditions?.phoneNumberIds ?? []);
  const [etiquetas, setEtiquetas] = useState<string[]>(regra?.conditions?.tagIds ?? []);
  const [horario, setHorario] = useState<Horario | null>(regra?.conditions?.schedule ?? null);
  const [acoes, setAcoes] = useState<Acao[]>(regra?.actions ?? []);
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [novaAcao, setNovaAcao] = useState<TipoAcao | ''>('');

  const listaTags = tags.state.status === 'ready' ? tags.state.data : [];
  const listaArquivos = arquivos.state.status === 'ready' ? arquivos.state.data.files : [];
  const listaIA = (integracoes.state.status === 'ready' ? integracoes.state.data : [])
    .filter((i) => i.kind === 'AI_AGENT' && i.enabled);
  const listaAgentes = (agentes.state.status === 'ready' ? agentes.state.data : [])
    .filter((agente) => agente.enabled);
  const listaNumeros = (canais.state.status === 'ready' ? canais.state.data : [])
    .flatMap((c) => c.phoneNumbers);

  const temTransferencia = acoes.some((a) => a.type === 'TRANSFER_HUMAN');

  function alterarAcao(indice: number, mudanca: Partial<Acao>) {
    setAcoes((atuais) => atuais.map((a, i) => (i === indice ? { ...a, ...mudanca } : a)));
  }

  function mover(indice: number, direcao: -1 | 1) {
    setAcoes((atuais) => {
      const destino = indice + direcao;
      if (destino < 0 || destino >= atuais.length) return atuais;
      const copia = [...atuais];
      const [item] = copia.splice(indice, 1);
      copia.splice(destino, 0, item!);
      return copia;
    });
  }

  /** Corpo exatamente no formato do schema — sem chaves a mais, que o
   *  `discriminatedUnion` recusa. */
  function corpo() {
    return {
      name: nome.trim(),
      keywords: palavras,
      matchType: casamento,
      priority: Number(prioridade) || 0,
      enabled: ativa,
      conditions: {
        phoneNumberIds: numeros,
        tagIds: etiquetas,
        ...(horario ? { schedule: horario } : {}),
      },
      actions: acoes.map((a) => {
        if (a.type === 'SEND_TEXT') return { type: a.type, body: a.body ?? '' };
        if (a.type === 'SEND_BUTTONS') {
          return {
            type: a.type, body: a.body ?? '',
            ...(a.footer ? { footer: a.footer } : {}),
            buttons: (a.buttons ?? []).map((b) => ({ id: b.id, title: b.title })),
          };
        }
        if (a.type === 'ADD_TAG') return { type: a.type, tagId: a.tagId };
        if (a.type === 'SEND_FILE') return { type: a.type, fileId: a.fileId, ...(a.caption ? { caption: a.caption } : {}) };
        if (a.type === 'AI_REPLY') return {
          type: a.type,
          ...(a.agentKey ? { agentKey: a.agentKey } : { integrationId: a.integrationId }),
          instructions: a.instructions ?? '',
        };
        return { type: a.type };
      }),
    };
  }

  /** Recusa o que o servidor recusaria, com a frase que aponta o campo. Não é
   *  duplicação de regra por gosto: a mensagem dele é uma só para o corpo
   *  inteiro ("Informe nome, palavras-chave, condições e ao menos uma ação
   *  válida"), e não diz qual dos quatro está errado. */
  function problema(): string | null {
    if (nome.trim().length < 1) return 'Dê um nome à regra.';
    if (palavras.length === 0) return 'Adicione ao menos uma palavra-chave.';
    if (acoes.length === 0) return 'Adicione ao menos uma ação.';
    if (acoes.length > 8) return 'Uma regra aceita no máximo oito ações.';
    const transferencias = acoes.flatMap((a, i) => (a.type === 'TRANSFER_HUMAN' ? [i] : []));
    if (transferencias.length > 1) return 'Use no máximo uma transferência para humano.';
    if (transferencias.length === 1 && transferencias[0] !== acoes.length - 1) {
      return 'A transferência para humano precisa ser a última ação: depois dela nenhuma outra roda.';
    }
    for (const [i, a] of acoes.entries()) {
      const onde = `Ação ${i + 1} (${ROTULO_ACAO[a.type]})`;
      if (a.type === 'SEND_TEXT' && !a.body?.trim()) return `${onde}: escreva o texto da resposta.`;
      if (a.type === 'SEND_BUTTONS') {
        if (!a.body?.trim()) return `${onde}: escreva a mensagem.`;
        if (!a.buttons?.length) return `${onde}: adicione ao menos um botão.`;
        if (a.buttons.some((b) => !b.title.trim())) return `${onde}: todo botão precisa de rótulo.`;
        if (a.buttons.some((b) => !/^[A-Za-z0-9_.:-]+$/.test(b.id))) return `${onde}: o identificador do botão aceita apenas letras, números, ponto, hífen, dois-pontos e sublinhado.`;
      }
      if (a.type === 'ADD_TAG' && !a.tagId) return `${onde}: escolha a etiqueta.`;
      if (a.type === 'SEND_FILE' && !a.fileId) return `${onde}: escolha o arquivo.`;
      if (a.type === 'AI_REPLY') {
        if (!a.agentKey && !a.integrationId) return `${onde}: escolha o agente de IA.`;
        if ((a.instructions ?? '').trim().length < 10) return `${onde}: as instruções precisam de ao menos 10 caracteres.`;
      }
    }
    if (horario && horario.days.length === 0) return 'Escolha ao menos um dia no horário de funcionamento.';
    return null;
  }

  async function enviar(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const impedimento = problema();
    if (impedimento) { setErro(impedimento); return; }
    setErro(null);
    setSalvando(true);
    try {
      if (editando) {
        await api(`/workspace/autoreplies/${regra!.id}`, { method: 'PUT', body: corpo() });
        onSalvo('Regra atualizada.');
      } else {
        await api('/workspace/autoreplies', { method: 'POST', body: corpo() });
        onSalvo('Regra criada.');
      }
    } catch (falha) {
      setErro(falha instanceof ApiError ? falha.message : 'Falha de conexão.');
    } finally {
      setSalvando(false);
    }
  }

  return (
    <Drawer
      open
      onClose={onFechar}
      title={editando ? 'Editar automação' : 'Nova automação'}
      description="A regra dispara quando a mensagem recebida casa com as palavras-chave e as condições. As ações rodam na ordem em que estão."
      footer={
        <>
          <Button variant="tertiary" onClick={onFechar}>Cancelar</Button>
          <Button
            variant="primary" type="submit" form="form-automacao"
            loading={salvando} loadingLabel="Salvando a regra"
            icon={<Check size={18} aria-hidden="true" />}
          >
            {editando ? 'Salvar alterações' : 'Criar regra'}
          </Button>
        </>
      }
    >
      <form id="form-automacao" onSubmit={enviar} className="auto-form">
        {erro && <Notice tone="danger" title="Revise a regra" live>{erro}</Notice>}

        <Secao titulo="Identificação">
          <Input label="Nome da regra" value={nome} onChange={(e) => setNome(e.currentTarget.value)}
            required maxLength={120} placeholder="Fora do horário" />
          <Input label="Prioridade" type="number" min={0} max={9999} value={prioridade}
            onChange={(e) => setPrioridade(e.currentTarget.value)}
            hint="Menor número roda primeiro. Duas regras que casam com a mesma palavra são resolvidas por aqui." />
          <Checkbox checked={ativa} onChange={(e) => setAtiva(e.currentTarget.checked)}>
            Regra ligada
          </Checkbox>
        </Secao>

        <Secao titulo="Gatilho">
          <div>
            <p className="auto-form__rotulo">Como comparar</p>
            <Segmented
              rotuloGrupo="Como comparar a mensagem com as palavras-chave"
              valor={casamento}
              onChange={setCasamento}
              opcoes={[
                { valor: 'CONTAINS', rotulo: 'Contém' },
                { valor: 'EXACT', rotulo: 'Igual a' },
                { valor: 'STARTS_WITH', rotulo: 'Começa com' },
              ]}
            />
          </div>
          <ListaTexto
            label="Palavras-chave"
            valores={palavras}
            onChange={setPalavras}
            max={30}
            placeholder="horário"
            hint="Enter ou vírgula adiciona. Basta uma delas casar para a regra disparar."
          />
        </Secao>

        <Secao titulo="Condições" descricao="Em branco significa “sem restrição”, não “nunca”.">
          <ListaDeMarcar
            titulo="Só nestes números"
            vazio="Nenhum número conectado."
            itens={listaNumeros.map((n) => ({ id: n.id, rotulo: n.displayNumber }))}
            selecionados={numeros}
            onChange={setNumeros}
          />
          <ListaDeMarcar
            titulo="Só para contatos com estas etiquetas"
            vazio="Nenhuma etiqueta cadastrada."
            itens={listaTags.map((t) => ({ id: t.id, rotulo: t.name }))}
            selecionados={etiquetas}
            onChange={setEtiquetas}
          />

          <Checkbox
            checked={horario !== null}
            onChange={(e) => setHorario(e.currentTarget.checked
              ? { days: [1, 2, 3, 4, 5], start: '08:00', end: '18:00', timezone: FUSO }
              : null)}
          >
            Só em determinado horário
          </Checkbox>

          {horario && (
            <div className="auto-horario">
              <div>
                <p className="auto-form__rotulo">Dias</p>
                <div className="auto-dias" role="group" aria-label="Dias da semana">
                  {DIAS.map((dia) => {
                    const marcado = horario.days.includes(dia.valor);
                    return (
                      <button
                        key={dia.valor}
                        type="button"
                        className="auto-dia"
                        aria-pressed={marcado}
                        onClick={() => setHorario({
                          ...horario,
                          days: marcado
                            ? horario.days.filter((d) => d !== dia.valor)
                            : [...horario.days, dia.valor].sort((a, b) => a - b),
                        })}
                      >
                        {dia.rotulo}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="auto-horario__faixa">
                <Input label="Início" type="time" value={horario.start} required
                  onChange={(e) => setHorario({ ...horario, start: e.currentTarget.value })} />
                <Input label="Fim" type="time" value={horario.end} required
                  onChange={(e) => setHorario({ ...horario, end: e.currentTarget.value })} />
              </div>
              <Input label="Fuso horário" value={horario.timezone} required
                onChange={(e) => setHorario({ ...horario, timezone: e.currentTarget.value })}
                hint="Preenchido com o fuso deste navegador. O servidor compara o horário nele, não no do cliente." />
            </div>
          )}
        </Secao>

        <Secao
          titulo="Ações"
          descricao="Rodam de cima para baixo. Até oito."
        >
          {acoes.length === 0 && (
            <p className="auto-form__vazio">Nenhuma ação ainda. Uma regra sem ação não faz nada.</p>
          )}

          <ol className="auto-acoes">
            {acoes.map((acao, indice) => (
              <li key={indice} className="auto-acao">
                <div className="auto-acao__topo">
                  <span className="auto-acao__nome">{indice + 1}. {ROTULO_ACAO[acao.type]}</span>
                  <div className="auto-acao__botoes">
                    <button type="button" className="auto-icone" aria-label={`Mover "${ROTULO_ACAO[acao.type]}" para cima`}
                      disabled={indice === 0} onClick={() => mover(indice, -1)}>
                      <ArrowUp size={16} aria-hidden="true" />
                    </button>
                    <button type="button" className="auto-icone" aria-label={`Mover "${ROTULO_ACAO[acao.type]}" para baixo`}
                      disabled={indice === acoes.length - 1} onClick={() => mover(indice, 1)}>
                      <ArrowDown size={16} aria-hidden="true" />
                    </button>
                    <button type="button" className="auto-icone auto-icone--perigo" aria-label={`Remover "${ROTULO_ACAO[acao.type]}"`}
                      onClick={() => setAcoes(acoes.filter((_, i) => i !== indice))}>
                      <Trash2 size={16} aria-hidden="true" />
                    </button>
                  </div>
                </div>

                {acao.type === 'SEND_TEXT' && (
                  <Textarea label="Texto da resposta" value={acao.body ?? ''} rows={3} maxLength={4096}
                    onChange={(e) => alterarAcao(indice, { body: e.currentTarget.value })} />
                )}

                {acao.type === 'SEND_BUTTONS' && (
                  <>
                    <Textarea label="Mensagem" value={acao.body ?? ''} rows={2} maxLength={1024}
                      onChange={(e) => alterarAcao(indice, { body: e.currentTarget.value })} />
                    <Input label="Rodapé" value={acao.footer ?? ''} maxLength={60}
                      onChange={(e) => alterarAcao(indice, { footer: e.currentTarget.value })}
                      hint="Opcional. Até 60 caracteres." />
                    {(acao.buttons ?? []).map((botao, j) => (
                      <div className="auto-botao" key={j}>
                        <Input label={`Botão ${j + 1}`} value={botao.title} maxLength={20}
                          onChange={(e) => alterarAcao(indice, {
                            buttons: (acao.buttons ?? []).map((b, k) => (k === j ? { ...b, title: e.currentTarget.value } : b)),
                          })}
                          hint="O WhatsApp corta em 20 caracteres." />
                        <Input label={`Identificador ${j + 1}`} value={botao.id}
                          onChange={(e) => alterarAcao(indice, {
                            buttons: (acao.buttons ?? []).map((b, k) => (k === j ? { ...b, id: e.currentTarget.value } : b)),
                          })}
                          hint="Volta na resposta do contato. Letras, números, . - : _" />
                        <Button variant="danger-quiet" size="sm" icon={<Trash2 size={16} aria-hidden="true" />}
                          onClick={() => alterarAcao(indice, { buttons: (acao.buttons ?? []).filter((_, k) => k !== j) })}>
                          Remover botão {j + 1}
                        </Button>
                      </div>
                    ))}
                    {(acao.buttons?.length ?? 0) < 3 && (
                      <Button variant="secondary" size="sm" icon={<Plus size={16} aria-hidden="true" />}
                        onClick={() => alterarAcao(indice, {
                          buttons: [...(acao.buttons ?? []), { id: `opcao_${(acao.buttons?.length ?? 0) + 1}`, title: '' }],
                        })}>
                        Adicionar botão
                      </Button>
                    )}
                  </>
                )}

                {acao.type === 'ADD_TAG' && (
                  <Select label="Etiqueta" value={acao.tagId ?? ''} placeholder="Selecione"
                    options={listaTags.map((t) => ({ value: t.id, label: t.name }))}
                    onChange={(e) => alterarAcao(indice, { tagId: e.currentTarget.value })} />
                )}

                {acao.type === 'SEND_FILE' && (
                  <>
                    <Select label="Arquivo" value={acao.fileId ?? ''} placeholder="Selecione"
                      options={listaArquivos.map((f) => ({ value: f.id, label: f.originalName }))}
                      onChange={(e) => alterarAcao(indice, { fileId: e.currentTarget.value })}
                      hint={listaArquivos.length === 0 ? 'A biblioteca está vazia. Envie um arquivo em Arquivos primeiro.' : undefined} />
                    <Input label="Legenda" value={acao.caption ?? ''} maxLength={1024}
                      onChange={(e) => alterarAcao(indice, { caption: e.currentTarget.value })} />
                  </>
                )}

                {acao.type === 'AI_REPLY' && (
                  <>
                    <Select label="Agente de IA" value={acao.agentKey ?? (acao.integrationId ? `legacy:${acao.integrationId}` : '')} placeholder="Selecione"
                      options={[
                        ...listaAgentes.map((agente) => ({ value: agente.key, label: agente.name })),
                        ...(acao.integrationId && !acao.agentKey
                          ? [{value:`legacy:${acao.integrationId}`,label:`Integração antiga · ${listaIA.find((item) => item.id === acao.integrationId)?.name ?? 'Agente de IA'}`}]
                          : []),
                      ]}
                      onChange={(e) => alterarAcao(indice, { agentKey: e.currentTarget.value, integrationId: undefined })}
                      hint={listaAgentes.length === 0
                        ? 'Nenhum agente ativo. Configure um em Agentes de IA.'
                        : 'O agente usa o provedor, o modelo, o raciocínio e as ferramentas configurados em Agentes de IA.'} />
                    <Textarea label="Instruções para o agente" value={acao.instructions ?? ''} rows={3} maxLength={4000}
                      onChange={(e) => alterarAcao(indice, { instructions: e.currentTarget.value })}
                      hint="Mínimo de 10 caracteres. Diga o que o agente deve fazer com a mensagem recebida." />
                  </>
                )}

                {acao.type === 'TRANSFER_HUMAN' && (
                  <p className="auto-acao__nota">
                    A conversa entra na fila de atendimento humano. Nenhuma ação posterior roda.
                  </p>
                )}
              </li>
            ))}
          </ol>

          {acoes.length < 8 && (
            <div className="auto-adicionar">
              <Select
                label="Adicionar ação"
                value={novaAcao}
                placeholder="Escolha o tipo"
                options={(Object.keys(ROTULO_ACAO) as TipoAcao[])
                  .filter((t) => t !== 'TRANSFER_HUMAN' || !temTransferencia)
                  .map((t) => ({ value: t, label: ROTULO_ACAO[t] }))}
                onChange={(e) => {
                  const tipo = e.currentTarget.value as TipoAcao;
                  if (!tipo) return;
                  // A transferência sempre entra no fim; qualquer outra entra
                  // ANTES dela, senão a regra nasceria inválida a cada adição.
                  setAcoes((atuais) => {
                    const nova = acaoNova(tipo);
                    if (tipo === 'TRANSFER_HUMAN') return [...atuais, nova];
                    const fim = atuais.length && atuais[atuais.length - 1]!.type === 'TRANSFER_HUMAN';
                    return fim
                      ? [...atuais.slice(0, -1), nova, atuais[atuais.length - 1]!]
                      : [...atuais, nova];
                  });
                  setNovaAcao('');
                }}
              />
            </div>
          )}
        </Secao>

        <Simulador corpo={corpo} problema={problema} numeros={listaNumeros} tags={listaTags} />
      </form>
    </Drawer>
  );
}

function Secao({ titulo, descricao, children }: { titulo: string; descricao?: string; children: ReactNode }) {
  return (
    <section className="auto-secao">
      <h3 className="auto-secao__titulo">{titulo}</h3>
      {descricao && <p className="auto-secao__sub">{descricao}</p>}
      <div className="auto-secao__corpo">{children}</div>
    </section>
  );
}

/** Conjunto de caixas de seleção. Um `<select multiple>` seria menor e pior:
 *  em telefone ele vira uma lista que exige toque preciso, e quem usa teclado
 *  precisa segurar Ctrl para marcar o segundo item. */
function ListaDeMarcar({
  titulo, vazio, itens, selecionados, onChange,
}: {
  titulo: string; vazio: string;
  itens: ReadonlyArray<{ id: string; rotulo: string }>;
  selecionados: string[];
  onChange: (ids: string[]) => void;
}) {
  return (
    <fieldset className="auto-marcar">
      <legend className="auto-form__rotulo">{titulo}</legend>
      {itens.length === 0 ? (
        <p className="auto-form__vazio">{vazio}</p>
      ) : (
        itens.map((item) => (
          <Checkbox
            key={item.id}
            checked={selecionados.includes(item.id)}
            onChange={(e) => onChange(e.currentTarget.checked
              ? [...selecionados, item.id]
              : selecionados.filter((id) => id !== item.id))}
          >
            {item.rotulo}
          </Checkbox>
        ))
      )}
    </fieldset>
  );
}

interface Resultado {
  matched: boolean;
  keywordMatch: boolean;
  conditionsMatch: boolean;
  actions: Array<{ position: number; type: string; summary: string }>;
}

/**
 * Simulador.
 *
 * `POST /autoreplies/simulate` existe desde sempre e nunca teve interface. Sem
 * ele, a única forma de saber se uma regra casa é publicar e esperar um cliente
 * real escrever — o que transforma cada ajuste num experimento em produção.
 *
 * O resultado separa palavra-chave de condição de propósito: "não casou" não
 * diz nada; "a palavra casou, o horário não" diz exatamente o que corrigir.
 */
function Simulador({
  corpo, problema, numeros, tags,
}: {
  corpo: () => unknown;
  problema: () => string | null;
  numeros: ReadonlyArray<{ id: string; displayNumber: string }>;
  tags: ReadonlyArray<{ id: string; name: string }>;
}) {
  const [mensagem, setMensagem] = useState('');
  const [numero, setNumero] = useState('');
  const [etiqueta, setEtiqueta] = useState('');
  const [resultado, setResultado] = useState<Resultado | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [rodando, setRodando] = useState(false);

  async function simular() {
    setErro(null);
    setResultado(null);
    const impedimento = problema();
    if (impedimento) { setErro(impedimento); return; }
    if (!numero) { setErro('Escolha o número que receberia a mensagem.'); return; }
    setRodando(true);
    try {
      setResultado(await api<Resultado>('/workspace/autoreplies/simulate', {
        method: 'POST',
        body: {
          rule: corpo(),
          sample: { message: mensagem, phoneNumberId: numero, tagIds: etiqueta ? [etiqueta] : [] },
        },
      }));
    } catch (falha) {
      setErro(falha instanceof ApiError ? falha.message : 'Falha de conexão.');
    } finally {
      setRodando(false);
    }
  }

  return (
    <Secao titulo="Simular" descricao="Testa esta regra contra uma mensagem de exemplo sem salvar nada.">
      <Input label="Mensagem de exemplo" value={mensagem} maxLength={4096}
        onChange={(e) => setMensagem(e.currentTarget.value)} placeholder="qual o horário de atendimento?" />
      <Select label="Número que recebeu" value={numero} placeholder="Selecione"
        options={numeros.map((n) => ({ value: n.id, label: n.displayNumber }))}
        onChange={(e) => setNumero(e.currentTarget.value)} />
      <Select label="Etiqueta do contato" value={etiqueta} placeholder="Nenhuma"
        options={tags.map((t) => ({ value: t.id, label: t.name }))}
        onChange={(e) => setEtiqueta(e.currentTarget.value)} />

      {/* `type="button"`: dentro do <form> da regra, o padrão seria submit e
          simular acabaria salvando. */}
      <Button type="button" variant="secondary" onClick={() => void simular()}
        loading={rodando} loadingLabel="Simulando" icon={<FlaskConical size={18} aria-hidden="true" />}>
        Simular
      </Button>

      {erro && <Notice tone="danger" title="Não foi possível simular" live>{erro}</Notice>}

      {resultado && (
        <Notice
          tone={resultado.matched ? 'success' : 'warning'}
          title={resultado.matched ? 'A regra dispararia' : 'A regra NÃO dispararia'}
          live
        >
          <ul className="auto-resultado">
            <li>Palavra-chave: {resultado.keywordMatch ? 'casou' : 'não casou'}</li>
            <li>Condições: {resultado.conditionsMatch ? 'atendidas' : 'não atendidas'}</li>
          </ul>
          {resultado.matched && (
            <ol className="auto-resultado">
              {resultado.actions.map((a) => (
                <li key={a.position}>{ROTULO_ACAO[a.type as TipoAcao] ?? a.type}: {a.summary}</li>
              ))}
            </ol>
          )}
        </Notice>
      )}
    </Secao>
  );
}
